import type { ListboxCatalogRow } from '@/common/utils/parseListboxCatalogFromColumnScript'
import { resolveListboxImageFromCatalogRows } from '@/common/utils/parseListboxCatalogFromColumnScript'
import { resolveSwingIconUrl } from '@/common/utils/resolvePlatformIcon'

export function listboxOptionValueFromRow(item: any): string | number | bigint | boolean | '' {
  const v =
    item?.fields?.code?.value ??
    item?.fields?.kind?.value ??
    item?.fields?.value?.value ??
    item?.fields?._id?.value ??
    item?.code?._text ??
    item?.kind?._text ??
    item?.value?._text ??
    item?._id?._text ??
    item?.id
  if (v === undefined || v === null) return ''
  return v as string | number | bigint | boolean
}

export function listboxRowHasExplicitDescription(item: any): boolean {
  if (!item || typeof item !== 'object') return false
  if (item.fields != null && typeof item.fields === 'object' && 'description' in item.fields) {
    return true
  }
  return 'description' in item && item.description !== undefined
}

export function listboxOptionTextFromRow(item: any): string {
  if (listboxRowHasExplicitDescription(item)) {
    const explicit =
      item?.fields?.description?.value ??
      item?.description?._text ??
      (typeof item?.description === 'string' ? item.description : undefined)
    if (explicit === undefined || explicit === null) return ''
    return String(explicit)
  }

  const t =
    item?.fields?.name?.value ??
    item?.fields?.fullName?.value ??
    item?.fields?.title?.value ??
    item?.fields?.numTitle?.value ??
    item?.fields?.fio?.value ??
    item?.name?._text ??
    item?.fullName?._text ??
    item?.title?._text ??
    item?.numTitle?._text ??
    item?.fio?._text ??
    item?.text ??
    item?.label
  if (t === undefined || t === null || t === '') return ''
  return String(t)
}

/** Подпись выбранного ListBox из Swing-поля name__text (TrimListBoxGetter). */
export function resolveListboxSelectedTextFromPageFields(
  pageFields: Record<string, any> | null | undefined,
  fieldName: string,
): string | undefined {
  if (!pageFields || !fieldName) return undefined
  const raw = pageFields[`${fieldName}__text`]
  if (raw == null) return undefined
  let text: unknown = raw
  if (typeof raw === 'object' && raw !== null && 'value' in raw) {
    text = (raw as { value: unknown }).value
  }
  if (text === undefined || text === null || text === '') return undefined
  return String(text)
}

/** Добавить выбранное значение в items, если его нет в справочнике (Swing __text + code). */
export function ensureListboxSelectedOptionVisible(
  pageFields: Record<string, any> | null | undefined,
  fieldName: string,
  items: any[],
): any[] {
  const name = String(fieldName ?? '').trim()
  if (!name) return items

  const selectedText = resolveListboxSelectedTextFromPageFields(pageFields, name)
  if (!selectedText) return items

  const rawField = pageFields?.[name]
  let selectedValue: unknown = rawField
  if (rawField != null && typeof rawField === 'object' && 'value' in rawField) {
    selectedValue = (rawField as { value: unknown }).value
  }
  if (selectedValue === undefined || selectedValue === null || selectedValue === '') {
    return items
  }
  const valueStr = String(selectedValue)
  if (items.some((item) => String(item?.value) === valueStr)) {
    return items
  }
  return [{ value: selectedValue, text: selectedText, label: selectedText }, ...items]
}

function getListboxRowsFromPageField(fieldData: any): any[] | undefined {
  if (!fieldData) return undefined
  const r = fieldData.value?.rows ?? fieldData.rows
  return Array.isArray(r) ? r : undefined
}

function normalizeListboxCodeStr(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return String(value)
  return String(value).trim()
}

function descriptionForCodeInRows(rows: any[], codeStr: string): string | undefined {
  const normalizedCode = normalizeListboxCodeStr(codeStr)
  for (const row of rows) {
    const optVal = listboxOptionValueFromRow(row)
    if (normalizeListboxCodeStr(optVal) !== normalizedCode) continue
    if (listboxRowHasExplicitDescription(row)) {
      return listboxOptionTextFromRow(row)
    }
    const text = listboxOptionTextFromRow(row)
    if (text) return text
  }
  return undefined
}

export function listboxOptionImageFromRow(item: any): string {
  const img =
    item?.fields?.image?.value ??
    item?.fields?.icon?.value ??
    item?.image?._text ??
    item?.image ??
    item?.icon?._text ??
    item?.icon
  if (img === undefined || img === null || img === '') return ''
  return String(img)
}

function imageForCodeInRows(rows: any[], codeStr: string): string | undefined {
  const normalizedCode = normalizeListboxCodeStr(codeStr)
  for (const row of rows) {
    const optVal = listboxOptionValueFromRow(row)
    if (normalizeListboxCodeStr(optVal) !== normalizedCode) continue
    const image = listboxOptionImageFromRow(row)
    if (image) return image
  }
  return undefined
}

function resolveImageByScanningListboxFields(
  pageFields: Record<string, any>,
  codeStr: string,
  columnFieldName: string,
  columnElementId?: string,
): string | undefined {
  const hits: { key: string; image: string }[] = []
  for (const key of Object.keys(pageFields)) {
    if (!key.endsWith('__listbox')) continue
    const rows = getListboxRowsFromPageField(pageFields[key])
    if (!rows?.length) continue
    const image = imageForCodeInRows(rows, codeStr)
    if (image) hits.push({ key, image })
  }
  if (hits.length === 0) return undefined
  if (hits.length === 1) return hits[0].image

  const idHint = columnElementId?.trim()
  if (idHint) {
    const byId = hits.filter(
      (h) =>
        h.key === `${idHint}__listbox` ||
        h.key.startsWith(`${idHint}__`) ||
        h.key.endsWith(`__${idHint}__listbox`),
    )
    if (byId.length === 1) return byId[0].image
  }

  const byCol = hits.filter(
    (h) =>
      columnFieldName &&
      (h.key === `${columnFieldName}__listbox` || h.key.includes(`__${columnFieldName}__`)),
  )
  if (byCol.length === 1) return byCol[0].image

  const uniqueImages = [...new Set(hits.map((h) => h.image))]
  if (uniqueImages.length === 1) return uniqueImages[0]

  return undefined
}

export type ListboxTableResolveOptions = {
  /** Имя поля TABLE на форме — для ключей вида `${tableFieldName}__${column}__listbox`. */
  tableFieldName?: string
  /**
   * Id элемента колонки из метаданных 
   */
  columnElementId?: string
  /** Поля строки таблицы — `{columnId}__listbox` и др. */
  rowFields?: Record<string, unknown> | null
}

function collectExplicitListboxKeys(
  columnFieldName: string,
  options?: ListboxTableResolveOptions,
): string[] {
  const bases: string[] = []
  if (columnFieldName) bases.push(columnFieldName)
  const elId = options?.columnElementId?.trim()
  if (elId && !bases.includes(elId)) bases.push(elId)
  const table = options?.tableFieldName?.trim()
  if (table && columnFieldName) {
    const combined = `${table}__${columnFieldName}`
    if (!bases.includes(combined)) bases.push(combined)
  }
  if (table && elId) {
    const swingCombined = `${table}__${elId}`
    if (!bases.includes(swingCombined)) bases.push(swingCombined)
  }

  const keys: string[] = []
  for (const base of bases) {
    keys.push(`${base}__listbox`, `${base}__items`)
  }
  return keys
}

/**
 * Если явных ключей нет — ищем среди всех `*__listbox`, где есть строка с данным code.
 */
function resolveByScanningListboxFields(
  pageFields: Record<string, any>,
  codeStr: string,
  columnFieldName: string,
  columnElementId?: string,
): string | undefined {
  const hits: { key: string; text: string }[] = []
  for (const key of Object.keys(pageFields)) {
    if (!key.endsWith('__listbox')) continue
    const rows = getListboxRowsFromPageField(pageFields[key])
    if (!rows?.length) continue
    const text = descriptionForCodeInRows(rows, codeStr)
    if (text !== undefined) hits.push({ key, text })
  }
  if (hits.length === 0) return undefined
  if (hits.length === 1) return hits[0].text

  const idHint = columnElementId?.trim()
  if (idHint) {
    const byId = hits.filter(
      (h) =>
        h.key === `${idHint}__listbox` ||
        h.key.startsWith(`${idHint}__`) ||
        h.key.endsWith(`__${idHint}__listbox`),
    )
    if (byId.length === 1) return byId[0].text
  }

  const byCol = hits.filter(
    (h) =>
      columnFieldName &&
      (h.key === `${columnFieldName}__listbox` || h.key.includes(`__${columnFieldName}__`)),
  )
  if (byCol.length === 1) return byCol[0].text

  const uniqueTexts = [...new Set(hits.map((h) => h.text))]
  if (uniqueTexts.length === 1) return uniqueTexts[0]

  return undefined
}

function resolveTextFromFieldMap(
  fieldMap: Record<string, any>,
  keys: string[],
  codeStr: string,
): string | undefined {
  for (const k of keys) {
    const rows = getListboxRowsFromPageField(fieldMap[k])
    if (!rows?.length) continue
    const text = descriptionForCodeInRows(rows, codeStr)
    if (text !== undefined) return text
  }
  return undefined
}

/**
 * Находит подпись по коду: column__listbox, elementId__listbox, table__column__listbox, затем скан * __listbox.
 */
export function resolveListboxCodeFromPageFields(
  pageFields: Record<string, any> | null | undefined,
  columnFieldName: string,
  cellValue: unknown,
  options?: ListboxTableResolveOptions,
): string | undefined {
  if (!columnFieldName) return undefined

  let code: unknown = cellValue
  if (code != null && typeof code === 'object' && 'value' in (code as any)) {
    code = (code as any).value
  }
  if (code === undefined || code === null || code === '') return undefined
  const codeStr = normalizeListboxCodeStr(code)

  const keys = collectExplicitListboxKeys(columnFieldName, options)
  const rowFields = options?.rowFields
  if (rowFields) {
    const fromRow = resolveTextFromFieldMap(rowFields as Record<string, any>, keys, codeStr)
    if (fromRow !== undefined) return fromRow
  }

  if (pageFields) {
    const fromPage = resolveTextFromFieldMap(pageFields, keys, codeStr)
    if (fromPage !== undefined) return fromPage

    const fromScan = resolveByScanningListboxFields(
      pageFields,
      codeStr,
      columnFieldName,
      options?.columnElementId,
    )
    if (fromScan !== undefined) return fromScan
  }

  return undefined
}

/**
 * Находит image по коду: column__listbox, elementId__listbox, table__column__listbox, затем scan * __listbox.
 */
export function resolveListboxImageFromPageFields(
  pageFields: Record<string, any> | null | undefined,
  columnFieldName: string,
  cellValue: unknown,
  options?: ListboxTableResolveOptions,
): string | undefined {
  if (!pageFields || !columnFieldName) return undefined

  let code: unknown = cellValue
  if (code != null && typeof code === 'object' && 'value' in (code as any)) {
    code = (code as any).value
  }
  if (code === undefined || code === null || code === '') return undefined
  const codeStr = normalizeListboxCodeStr(code)

  let hasExplicitCatalog = false
  for (const k of collectExplicitListboxKeys(columnFieldName, options)) {
    const rows = getListboxRowsFromPageField(pageFields[k])
    if (!rows?.length) continue
    hasExplicitCatalog = true
    const image = imageForCodeInRows(rows, codeStr)
    if (image) return image
  }

  if (hasExplicitCatalog) {
    return undefined
  }

  return resolveImageByScanningListboxFields(
    pageFields,
    codeStr,
    columnFieldName,
    options?.columnElementId,
  )
}

function getListboxRowsFromRowFields(
  rowFields: Record<string, unknown> | null | undefined,
  columnFieldName: string,
  options?: ListboxTableResolveOptions,
): any[] | undefined {
  if (!rowFields) return undefined

  for (const key of collectExplicitListboxKeys(columnFieldName, options)) {
    const rows = getListboxRowsFromPageField((rowFields as any)[key])
    if (rows?.length) return rows
  }

  return undefined
}

export type ListboxSelectOption = {
  value: string | number | bigint | boolean
  text: string
  label: string
}

export function mapListboxRowsToSelectOptions(rows: readonly unknown[]): ListboxSelectOption[] {
  const options: ListboxSelectOption[] = []
  const seen = new Set<string>()

  for (const row of rows) {
    const value = listboxOptionValueFromRow(row)
    const text = listboxOptionTextFromRow(row)
    if (value === '' && text === '') continue

    const key = normalizeListboxCodeStr(value)
    if (seen.has(key)) continue
    seen.add(key)

    const label = listboxRowHasExplicitDescription(row) ? text : text || key
    options.push({ value, text: label, label })
  }

  return options
}

/**
 * Swing getCellModel parity: row-level `{columnId}__listbox` first, then page-level keys.
 */
export function resolveListboxOptionRowsForTableColumn(
  pageFields: Record<string, any> | null | undefined,
  columnFieldName: string,
  options?: ListboxTableResolveOptions,
): any[] {
  if (!columnFieldName) return []

  const fromRow = getListboxRowsFromRowFields(options?.rowFields, columnFieldName, options)
  if (fromRow?.length) return fromRow

  if (pageFields) {
    for (const key of collectExplicitListboxKeys(columnFieldName, options)) {
      const rows = getListboxRowsFromPageField(pageFields[key])
      if (rows?.length) return rows
    }
  }

  return []
}

export type ListboxImageResolveOptions = ListboxTableResolveOptions & {
  catalogRows?: ListboxCatalogRow[]
}

function normalizeListboxCellCode(cellValue: unknown): string {
  let code: unknown = cellValue
  if (code != null && typeof code === 'object' && 'value' in (code as any)) {
    code = (code as any).value
  }
  if (code === undefined || code === null || code === '') return ''
  return normalizeListboxCodeStr(code)
}

/** PNG/GIF/… из каталога или swingicons; логический code (servicesBanner) — нет. */
function isResolvedListboxAssetFile(image: string | undefined, cellCode: string): boolean {
  const trimmed = image?.trim()
  if (!trimmed || trimmed === cellCode) return false
  if (/\.(png|gif|jpg|jpeg|svg)$/i.test(trimmed)) {
    return Boolean(resolveSwingIconUrl(trimmed))
  }
  return Boolean(resolveSwingIconUrl(trimmed))
}

/** catalog (column onLoad) → pageFields __listbox → row.fields `{columnId}__listbox`. */
export function resolveListboxImage(
  pageFields: Record<string, any> | null | undefined,
  columnFieldName: string,
  cellValue: unknown,
  options?: ListboxImageResolveOptions,
): string | undefined {
  const cellCode = normalizeListboxCellCode(cellValue)

  const fromCatalog = resolveListboxImageFromCatalogRows(options?.catalogRows, cellValue)
  if (fromCatalog && isResolvedListboxAssetFile(fromCatalog, cellCode)) {
    return fromCatalog
  }

  const fromPage = resolveListboxImageFromPageFields(pageFields, columnFieldName, cellValue, options)
  if (fromPage && isResolvedListboxAssetFile(fromPage, cellCode)) {
    return fromPage
  }

  const rows = getListboxRowsFromRowFields(options?.rowFields, columnFieldName, options)
  if (rows?.length && cellCode !== '') {
    const fromRow = imageForCodeInRows(rows, cellCode)
    if (fromRow && isResolvedListboxAssetFile(fromRow, cellCode)) {
      return fromRow
    }
  }

  if (fromCatalog) return fromCatalog
  if (fromPage && fromPage !== cellCode) return fromPage

  return undefined
}
